import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { VoiceAgentService } from "@/services/integrations/elevenlabs/VoiceAgentService";

/** Agent voice volume restored whenever the agent's output is un-muted. */
export const DEFAULT_AGENT_VOICE_VOLUME = 0.85;

/** The slice of useVoiceAgentConversation's return this hook needs. */
interface VoiceAgentLike {
  connected: boolean;
  getService: () => VoiceAgentService | null;
}

interface UseAudioSessionControlsOptions {
  voiceAgent: VoiceAgentLike;
  /**
   * Called after the agent's voice is muted — the full page uses this to open
   * the transcript so the Genie's reply stays visible while silent.
   */
  onAgentVoiceMuted?: () => void;
}

/**
 * Owns the mic / agent-voice / pause state and the four audio toggle handlers
 * shared by the live-agent page and the embedded experience. Pause is a
 * phone-style hold: mic + agent voice mute together, the audio queue is
 * flushed so the agent doesn't finish its sentence on resume, and prior mute
 * states are restored exactly on resume.
 *
 * All flags reset when the conversation disconnects.
 */
export const useAudioSessionControls = ({
  voiceAgent,
  onAgentVoiceMuted,
}: UseAudioSessionControlsOptions) => {
  const [isMicMuted, setIsMicMuted] = useState(false);
  const [isAgentVoiceMuted, setIsAgentVoiceMuted] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const prePauseMicMutedRef = useRef(false);
  const prePauseAgentMutedRef = useRef(false);
  const pauseActivityIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const isAllAudioMuted = useMemo(
    () => isMicMuted && isAgentVoiceMuted,
    [isMicMuted, isAgentVoiceMuted],
  );

  // Reset split audio UI state when the conversation disconnects.
  useEffect(() => {
    if (!voiceAgent.connected) {
      if (pauseActivityIntervalRef.current) {
        clearInterval(pauseActivityIntervalRef.current);
        pauseActivityIntervalRef.current = null;
      }
      setIsMicMuted(false);
      setIsAgentVoiceMuted(false);
      setIsPaused(false);
    }
  }, [voiceAgent.connected]);

  const toggleMicMute = useCallback(async () => {
    const service = voiceAgent.getService();
    if (!service) return;
    const next = !isMicMuted;
    try {
      await service.setMicMuted(next);
      setIsMicMuted(next);
    } catch (error) {
      console.error("Error toggling microphone mute", error);
    }
  }, [voiceAgent, isMicMuted]);

  const toggleAgentVoice = useCallback(async () => {
    const service = voiceAgent.getService();
    if (!service) return;
    try {
      if (isAgentVoiceMuted) {
        await service.setVolume(DEFAULT_AGENT_VOICE_VOLUME);
        setIsAgentVoiceMuted(false);
      } else {
        await service.setVolume(0);
        setIsAgentVoiceMuted(true);
        onAgentVoiceMuted?.();
      }
    } catch (error) {
      console.error("Error toggling agent voice output", error);
    }
  }, [voiceAgent, isAgentVoiceMuted, onAgentVoiceMuted]);

  const togglePause = useCallback(async () => {
    const service = voiceAgent.getService();
    if (!service) return;
    try {
      if (isPaused) {
        if (pauseActivityIntervalRef.current) {
          clearInterval(pauseActivityIntervalRef.current);
          pauseActivityIntervalRef.current = null;
        }
        service.sendContextualUpdate("The user has resumed the conversation.");
        // Flush anything the server queued up during the pause before we
        // bring the volume back, so resume starts from clean silence.
        service.interruptCurrentSpeech();
        await service.setMicMuted(prePauseMicMutedRef.current);
        await service.setVolume(prePauseAgentMutedRef.current ? 0 : DEFAULT_AGENT_VOICE_VOLUME);
        setIsMicMuted(prePauseMicMutedRef.current);
        setIsAgentVoiceMuted(prePauseAgentMutedRef.current);
        setIsPaused(false);
      } else {
        prePauseMicMutedRef.current = isMicMuted;
        prePauseAgentMutedRef.current = isAgentVoiceMuted;
        service.sendContextualUpdate(
          "The user has paused the conversation and will return shortly. Do not interpret the silence as disengagement — wait for them to resume.",
        );
        await service.setMicMuted(true);
        // setVolume(0) now hard-cuts gain and flushes the audio worklet
        // buffer, so the agent stops mid-sentence instead of playing out.
        await service.setVolume(0);
        setIsMicMuted(true);
        setIsAgentVoiceMuted(true);
        setIsPaused(true);
        // Ping every 1.5s — sendUserActivity suppresses the agent's silence
        // timeout for ~2s per call, so this keeps it held indefinitely while paused.
        pauseActivityIntervalRef.current = setInterval(() => {
          service.sendUserActivity();
        }, 1500);
      }
    } catch (error) {
      console.error("Error toggling pause", error);
    }
  }, [voiceAgent, isPaused, isMicMuted, isAgentVoiceMuted]);

  const toggleAllAudioMute = useCallback(async () => {
    const service = voiceAgent.getService();
    if (!service) return;
    try {
      if (isAllAudioMuted) {
        await service.setMicMuted(false);
        await service.setVolume(DEFAULT_AGENT_VOICE_VOLUME);
        setIsMicMuted(false);
        setIsAgentVoiceMuted(false);
      } else {
        await service.setMicMuted(true);
        await service.setVolume(0);
        setIsMicMuted(true);
        setIsAgentVoiceMuted(true);
      }
    } catch (error) {
      console.error("Error toggling all audio channels", error);
    }
  }, [voiceAgent, isAllAudioMuted]);

  return {
    isMicMuted,
    setIsMicMuted,
    isAgentVoiceMuted,
    setIsAgentVoiceMuted,
    isPaused,
    isAllAudioMuted,
    toggleMicMute,
    toggleAgentVoice,
    togglePause,
    toggleAllAudioMute,
  };
};
